import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { EmptyState } from '@/shared/components';
import { useAuth } from '@/shared/auth';
import { useUserMutations } from '../useUserMutations';
import { UserAccessCard } from './UserAccessCard';
import { UserActivityCard } from './UserActivityCard';
import { CLOSED_USER_DIALOGS, UserDetailDialogs, type UserDialogState } from './UserDetailDialogs';
import { UserDetailFacts } from './UserDetailFacts';
import { UserDetailHeader } from './UserDetailHeader';
import { UserMasterDataCard } from './UserMasterDataCard';
import { UserPlatformRoleSection } from './UserPlatformRoleSection';
import { UserSecurityCard } from './UserSecurityCard';
import { useUserDetail } from './useUserDetail';

/**
 * Die Benutzerakte — Stammdaten, Zugriff und Sicherheit eines Kontos an einem Ort.
 *
 * <p>Informationshierarchie nach §4: der Kopf beantwortet „wer ist das und in welchem Zustand",
 * das Faktenband „was darf er", danach zwei Spalten. Links alles, was man an diesem Konto tut
 * (Stammdaten, Rolle, Zuweisungen, Sicherheit), rechts, was mit ihm passiert ist. Unter 1280 px
 * wird gestapelt, die Arbeitsspalte zuerst.</p>
 *
 * <p>Der Zugriffsbereich kommt als serverautoritatives Read-Modell; Bearbeitung und Vorschau
 * bleiben damit ein einzelner, nachvollziehbarer Zugriffsvorgang.</p>
 */
export function UserDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const currentUserId = useAuth().user?.userId;
  const { user, access, company, activity } = useUserDetail(id);

  const [dialogs, setDialogs] = useState<UserDialogState>(CLOSED_USER_DIALOGS);
  const patchDialogs = (patch: Partial<UserDialogState>) =>
    setDialogs((current) => ({ ...current, ...patch }));

  const mutations = useUserMutations({
    onDeactivated: () => patchDialogs({ deactivate: false }),
    onRenamed: () => patchDialogs({ name: false }),
    onAccessUpdated: () => patchDialogs({ access: false }),
    onTwoFactorReset: () => patchDialogs({ twoFactorReset: false }),
  });

  if (user.isError) {
    return (
      <EmptyState
        icon="Error"
        title="Diesen Benutzer gibt es hier nicht"
        description="Er wurde entfernt oder gehört zu einem anderen Workspace."
        action={{
          label: 'Zurück zur Benutzerliste',
          onClick: () => void navigate({ to: '/users' }),
        }}
      />
    );
  }

  if (!user.data) {
    return (
      <div
        role="status"
        aria-label="Benutzer wird geladen"
        className="border-line bg-card animate-pulse space-y-px overflow-hidden rounded-lg border"
      >
        <div className="bg-card-alt/60 h-28" />
        <div className="bg-card-alt/60 h-24" />
        <div className="bg-card-alt/60 h-64" />
      </div>
    );
  }

  const u = user.data;

  return (
    <div className="space-y-6">
      <UserDetailHeader user={u} />
      <UserDetailFacts
        user={u}
        company={company.data ?? null}
        assignedCount={access.data?.directProperties.length ?? 0}
      />

      {/* Links alles, was man an diesem Konto tut; rechts, was mit ihm passiert ist. Die
          Zeitleiste allein rechts, weil sie als einzige Karte beliebig lang wird — stünde sie
          unter der Sicherheit, endete die linke Spalte auf halber Höhe im Nichts. */}
      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <UserMasterDataCard
            user={u}
            company={company.data ?? null}
            companyLoading={company.isLoading}
            onEditName={() => patchDialogs({ name: true })}
          />
          <UserAccessCard
            access={access.data ?? null}
            company={company.data ?? null}
            loading={access.isLoading}
            error={access.isError}
            onEditAccess={() => patchDialogs({ access: true })}
            onRetry={() => void access.refetch()}
          />
          <UserSecurityCard
            user={u}
            isSelf={u.id === currentUserId}
            resendPending={mutations.resend.isPending}
            reactivatePending={mutations.reactivate.isPending}
            onResendInvitation={() => mutations.resend.mutate(u.id)}
            onResetTwoFactor={() => patchDialogs({ twoFactorReset: true })}
            onDeactivate={() => patchDialogs({ deactivate: true })}
            onReactivate={() => mutations.reactivate.mutate(u.id)}
          />
          <UserPlatformRoleSection user={u} isSelf={u.id === currentUserId} />
        </div>

        <UserActivityCard
          user={u}
          auditEntries={activity.entries}
          canReadAuditLog={activity.canReadAuditLog}
          auditLoading={activity.loading}
          auditError={activity.error}
          truncated={activity.truncated}
        />
      </div>

      <UserDetailDialogs
        user={u}
        access={access.data ?? null}
        state={dialogs}
        onChange={patchDialogs}
        mutations={mutations}
      />
    </div>
  );
}
